import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Wallet,
  Building,
  CreditCard,
  Calendar,
  RefreshCw,
  Upload,
  DollarSign,
  Brain,
  Trash2,
  AlertTriangle,
  CheckSquare,
  Square,
  X,
  CheckCircle,
  Receipt,
  Plus,
  FileText,
  Flag,
  Eye,
  Banknote
} from 'lucide-react';
import apiService from '../../services/api';
import ImportTransactionsModal from '../modals/ImportTransactionsModal';
import AddTransactionModal from '../modals/AddTransactionModal';
import AiAnalysisModal from '../modals/AiAnalysisModal';
import BulkCategoryUpdateModal from '../modals/BulkCategoryUpdateModal';
import TransactionDetailsModal from '../modals/TransactionDetailsModal';
import DuplicateDetailsModal from '../modals/DuplicateDetailsModal';
import ReceiptModal from '../modals/ReceiptModal';
import AddStatementModal from '../modals/AddStatementModal';
import ViewStatementsModal from '../modals/ViewStatementsModal';
import PageHeader from '../common/PageHeader';
import Pagination from '../common/Pagination';
import Holdings from '../Holdings';
import { useConfirmation } from '../../hooks/useConfirmation';
import { useToast } from '../../hooks/useToast';
import { formatDateShort } from '../../utils/dateUtils';

const AccountTab = ({ accountId }) => {
  const navigate = useNavigate();

  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isAddTransactionModalOpen, setIsAddTransactionModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isAiAnalysisModalOpen, setIsAiAnalysisModalOpen] = useState(false);
  const [analysisJobId, setAnalysisJobId] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [bulkUpdateModal, setBulkUpdateModal] = useState({
    isOpen: false,
    transactionId: null,
    description: '',
    newCategory: '',
    newCategoryIcon: '',
    categoryLabel: ''
  });
  const [selectedTransactions, setSelectedTransactions] = useState(new Set());
  const [duplicates, setDuplicates] = useState([]);
  const [detectingDuplicates, setDetectingDuplicates] = useState(false);
  const [highlightedTransaction, setHighlightedTransaction] = useState(null);
  const [duplicateResults, setDuplicateResults] = useState(null);
  const [isDuplicateDetailsModalOpen, setIsDuplicateDetailsModalOpen] = useState(false);
  const [holdings, setHoldings] = useState([]);
  const [holdingsLoading, setHoldingsLoading] = useState(false);
  const [totalMarketValue, setTotalMarketValue] = useState(0);
  const [viewReceiptModal, setViewReceiptModal] = useState({
    isOpen: false,
    receipt: null,
    currency: 'USD'
  });
  const [accounts, setAccounts] = useState([]);
  const [statements, setStatements] = useState([]);
  const [statementsLoading, setStatementsLoading] = useState(false);
  const [isAddStatementModalOpen, setIsAddStatementModalOpen] = useState(false);
  const [isViewStatementsModalOpen, setIsViewStatementsModalOpen] = useState(false);
  const [statementFilter, setStatementFilter] = useState('none');
  const { confirm, ConfirmationModal } = useConfirmation();
  const { showToast, ToastContainer } = useToast();

  const loadCategories = useCallback(async () => {
    try {
      const categories = await apiService.getCategories();
      // Transform database categories to match the expected format
      const categoryOptions = categories.map(cat => ({
        value: cat.name,
        label: cat.label,
        icon: cat.icon
      }));
      setCategoryOptions(categoryOptions);
    } catch (err) {
      console.error('Error loading categories:', err);
      // Fallback to a basic set if categories fail to load
      setCategoryOptions([
        { value: 'other', label: 'Other', icon: '💰' }
      ]);
    }
  }, []);

  const loadAccountData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load account details
      const accountData = await apiService.getAccount(accountId);
      setAccount(accountData);

      // Load all accounts for receipt modal
      const accountsData = await apiService.getAccounts();
      setAccounts(accountsData);

      // Load transactions with pagination
      const response = await apiService.getAccountTransactions(accountId, {
        page: currentPage,
        page_size: pageSize,
        filter_type: filterType !== 'all' ? filterType : null,
        statement_id: statementFilter !== 'all' ? (statementFilter === 'none' ? 0 : parseInt(statementFilter)) : null
      });

      setTransactions(response.transactions);
      setTotalCount(response.pagination.total);

      // Load holdings for investment accounts
      try {
        setHoldingsLoading(true);
        const holdingsData = await apiService.getAccountHoldings(accountId);
        setHoldings(holdingsData.holdings || []);
        setTotalMarketValue(holdingsData.total_market_value || 0);
      } catch (holdingsErr) {
        // Holdings may not exist for non-investment accounts, that's okay
        setHoldings([]);
        setTotalMarketValue(0);
      } finally {
        setHoldingsLoading(false);
      }
    } catch (err) {
      console.error('Error loading account data:', err);
      setError('Failed to load account data');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [accountId, currentPage, pageSize, filterType, statementFilter]);

  const loadStatements = useCallback(async () => {
    try {
      setStatementsLoading(true);
      const statementsData = await apiService.getStatements({
        account_id: accountId
      });
      setStatements(statementsData);
    } catch (err) {
      console.error('Error loading statements:', err);
      // Silent fail on load - user doesn't need toast for every page load
    } finally {
      setStatementsLoading(false);
    }
  }, [accountId]);

  const handleMarkStatementPaid = async (statementId) => {
    try {
      await apiService.markStatementPaid(statementId);
      showToast('Statement marked as paid', 'success');
      await loadStatements();
    } catch (err) {
      console.error('Error marking statement as paid:', err);
      showToast('Failed to mark statement as paid', 'error');
    }
  };

  const handleMarkStatementUnpaid = async (statementId) => {
    try {
      await apiService.markStatementUnpaid(statementId);
      showToast('Statement marked as unpaid', 'success');
      await loadStatements();
    } catch (err) {
      console.error('Error marking statement as unpaid:', err);
      showToast('Failed to mark statement as unpaid', 'error');
    }
  };

  const handleUpdateStatement = async (statementId, updates) => {
    try {
      await apiService.updateStatement(statementId, updates);
      showToast('Statement updated', 'success');
      await loadStatements();
    } catch (err) {
      console.error('Error updating statement:', err);
      showToast('Failed to update statement', 'error');
    }
  };

  const handleDeleteStatement = async (statementId) => {
    const confirmed = await confirm(
      'Delete Statement',
      'Are you sure you want to delete this statement? This will not delete the transactions.',
      'danger',
      'Delete',
      'Cancel'
    );

    if (confirmed) {
      try {
        await apiService.deleteStatement(statementId);
        showToast('Statement deleted successfully', 'success');
        await loadStatements();
      } catch (err) {
        console.error('Error deleting statement:', err);
        showToast('Failed to delete statement', 'error');
      }
    }
  };

  const handleRecalculateDueDates = async () => {
    try {
      const result = await apiService.recalculateStatementDueDates(accountId);
      showToast(`Updated ${result.statements_updated} statement due dates`, 'success');
      await loadStatements();
    } catch (err) {
      console.error('Error recalculating due dates:', err);
      showToast(err.message || 'Failed to recalculate due dates', 'error');
    }
  };

  const handleMarkStatementEnd = async (transaction) => {
    const confirmed = await confirm(
      'Mark as Statement End',
      `Mark transaction from ${formatDate(transaction.transaction_date)} as statement end?\n\nThis will:\n• Create a new statement with closing date ${formatDate(transaction.transaction_date)}\n• Auto-calculate statement balance from all transactions in the period\n• Assign related transactions to this statement`,
      'primary',
      'Create Statement',
      'Cancel'
    );

    if (confirmed) {
      try {
        const result = await apiService.markTransactionAsStatementEnd(transaction.id);
        showToast(`Statement created: ${result.statement.transaction_count} transactions, balance $${result.statement.statement_balance.toFixed(2)}`, 'success');
        await loadStatements();
        await loadTransactions();
        setIsTransactionModalOpen(false); // Close the transaction details modal
      } catch (err) {
        console.error('Error marking statement end:', err);
        const errorMessage = err.response?.data?.detail || err.message || 'Failed to mark statement end';
        showToast(errorMessage, 'error');
      }
    }
  };

  useEffect(() => {
    loadCategories();
    if (accountId) {
      loadAccountData();
      loadStatements();
    }
  }, [accountId, loadAccountData, loadCategories]);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadTransactions = async () => {
    try {
      setTransactionsLoading(true);
      const response = await apiService.getAccountTransactions(accountId, {
        page: currentPage,
        page_size: pageSize,
        filter_type: filterType !== 'all' ? filterType : null,
        statement_id: statementFilter !== 'all' ? (statementFilter === 'none' ? 0 : parseInt(statementFilter)) : null
      });
      setTransactions(response.transactions);
      setTotalCount(response.pagination.total);
    } catch (err) {
      console.error('Error loading transactions:', err);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleTransactionClick = (transaction, event) => {
    // Don't open modal if checkbox was clicked
    if (event.target.type === 'checkbox') {
      return;
    }
    setSelectedTransaction(transaction);
    setIsTransactionModalOpen(true);
  };

  const handleViewReceipt = async (e, receipt) => {
    e.stopPropagation();
    try {
      // Fetch full receipt with items
      const fullReceipt = await apiService.getReceipt(receipt.id);
      setViewReceiptModal({
        isOpen: true,
        receipt: fullReceipt,
        currency: account?.currency || 'USD'
      });
    } catch (err) {
      console.error('Error loading receipt:', err);
      // Fallback to showing what we have
      setViewReceiptModal({
        isOpen: true,
        receipt: receipt,
        currency: account?.currency || 'USD'
      });
    }
  };

  const handleCloseReceiptModal = () => {
    setViewReceiptModal({
      isOpen: false,
      receipt: null,
      currency: 'USD'
    });
    loadTransactions(); // Reload in case receipt was unlinked
  };

  const handleSelectTransaction = (transactionId) => {
    const newSelection = new Set(selectedTransactions);
    if (newSelection.has(transactionId)) {
      newSelection.delete(transactionId);
    } else {
      newSelection.add(transactionId);
    }
    setSelectedTransactions(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedTransactions.size === filteredTransactions.length) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(filteredTransactions.map(t => t.id)));
    }
  };

  const handleDetectDuplicates = async () => {
    try {
      setDetectingDuplicates(true);
      const response = await apiService.detectDuplicateTransactions(accountId);
      setDuplicates(response.duplicates);
      setDuplicateResults(response);

      if (response.duplicate_count > 0) {
        // Auto-select duplicate transactions for deletion (not originals)
        const duplicateIds = response.duplicates.map(d => d.duplicate_id);
        setSelectedTransactions(new Set(duplicateIds));
      }
    } catch (err) {
      console.error('Error detecting duplicates:', err);
      setDuplicateResults({ error: 'Failed to detect duplicates' });
    } finally {
      setDetectingDuplicates(false);
    }
  };

  const handleDismissResults = () => {
    setDuplicateResults(null);
    setDuplicates([]);
    setSelectedTransactions(new Set());
    setHighlightedTransaction(null);
  };

  const handleShowOriginal = (duplicateId) => {
    const duplicateInfo = duplicates.find(d => d.duplicate_id === duplicateId);
    if (duplicateInfo) {
      setHighlightedTransaction(duplicateInfo.original_id);
      // Scroll to the original transaction
      setTimeout(() => {
        const element = document.getElementById(`transaction-${duplicateInfo.original_id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      // Clear highlight after 3 seconds
      setTimeout(() => setHighlightedTransaction(null), 3000);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTransactions.size === 0) {
      showToast('Please select transactions to delete', 'error');
      return;
    }

    const confirmed = await confirm(
      'Delete Transactions',
      `Are you sure you want to delete ${selectedTransactions.size} transaction(s)?`,
      'danger',
      'Delete',
      'Cancel'
    );
    if (!confirmed) return;

    try {
      const response = await apiService.bulkDeleteTransactions(Array.from(selectedTransactions));
      showToast(response.message, 'success');

      // Refresh transactions and clear selection
      await loadTransactions();
      setSelectedTransactions(new Set());
      setDuplicates([]);
      setHighlightedTransaction(null);
      setDuplicateResults(null);
    } catch (err) {
      console.error('Error deleting transactions:', err);
      showToast('Failed to delete transactions', 'error');
    }
  };

  const handleTransactionSave = async (transactionId, updates) => {
    try {
      // Check if category changed and if we should offer bulk update
      const originalTransaction = transactions.find(tx => tx.id === transactionId);
      const categoryChanged = originalTransaction && updates.ai_category !== originalTransaction.ai_category;

      if (categoryChanged) {
        // Close transaction modal first
        setIsTransactionModalOpen(false);

        // Open bulk update modal to ask if user wants to update similar transactions
        const categoryOption = categoryOptions.find(opt => opt.value === updates.ai_category);
        setBulkUpdateModal({
          isOpen: true,
          transactionId: transactionId,
          description: originalTransaction.description,
          newCategory: updates.ai_category,
          newCategoryIcon: updates.category_icon || categoryOption?.icon || '💰',
          categoryLabel: categoryOption?.label || updates.ai_category
        });
      } else {
        // Just update the single transaction
        await apiService.updateTransaction(transactionId, updates);

        // Refresh account data to update balance
        await loadAccountData();

        setIsTransactionModalOpen(false);
      }
    } catch (err) {
      console.error('Error saving transaction:', err);
      throw err; // Re-throw so modal can handle error display
    }
  };

  const handleTransactionDelete = async (transactionId) => {
    try {
      await apiService.deleteTransaction(transactionId);

      // Update UI
      setTransactions(prevTransactions =>
        prevTransactions.filter(tx => tx.id !== transactionId)
      );

      // Refresh account data to update balance
      await loadAccountData();
    } catch (err) {
      console.error('Error deleting transaction:', err);
      throw err; // Re-throw so modal can handle error display
    }
  };

  const handleBulkUpdateConfirm = async (transactionIdsOrFalse) => {
    try {
      const { transactionId, newCategory, newCategoryIcon } = bulkUpdateModal;

      if (transactionIdsOrFalse === false) {
        // Update only the single original transaction
        await apiService.updateTransaction(transactionId, {
          ai_category: newCategory,
          category_icon: newCategoryIcon
        });

        // Update UI for single transaction
        setTransactions(prevTransactions =>
          prevTransactions.map(tx =>
            tx.id === transactionId
              ? { ...tx, ai_category: newCategory, category_icon: newCategoryIcon }
              : tx
          )
        );
      } else {
        // Bulk update selected transactions
        const transactionIds = Array.isArray(transactionIdsOrFalse) ? transactionIdsOrFalse : [];

        if (transactionIds.length > 0) {
          const response = await apiService.bulkUpdateTransactionCategory(
            transactionIds,
            newCategory,
            newCategoryIcon
          );

          // Update UI for all affected transactions
          setTransactions(prevTransactions =>
            prevTransactions.map(tx =>
              transactionIds.includes(tx.id)
                ? { ...tx, ai_category: newCategory, category_icon: newCategoryIcon }
                : tx
            )
          );
        }
      }

      // Close modal
      setBulkUpdateModal({
        isOpen: false,
        transactionId: null,
        description: '',
        newCategory: '',
        newCategoryIcon: '',
        categoryLabel: ''
      });

    } catch (err) {
      console.error('Error updating transaction category:', err);
      // Revert optimistic update on error
      loadTransactions();
      showToast('Failed to update transaction category. Please try again.', 'error');
    }
  };

  const handleBulkUpdateCancel = () => {
    setBulkUpdateModal({
      isOpen: false,
      transactionId: null,
      description: '',
      newCategory: '',
      newCategoryIcon: '',
      categoryLabel: ''
    });
  };


  const handleBackToAccounts = () => {
    // Navigate back to accounts tab
    navigate('/dashboard?tab=accounts');
  };

  const handleTransactionsImported = (response) => {
    // Refresh both account data (for balance) and transactions after import
    loadAccountData();
    // Close modal
    setIsImportModalOpen(false);
  };

  const handleTransactionAdded = async (transactionData) => {
    try {
      await apiService.createTransaction(transactionData);
      // Refresh both account data (for balance) and transactions after adding
      await loadAccountData();
      setIsAddTransactionModalOpen(false);
      showToast('Transaction added successfully', 'success');
    } catch (err) {
      console.error('Error adding transaction:', err);
      throw err; // Let the modal handle the error display
    }
  };

  const handleAiAnalysis = () => {
    if (totalCount === 0) {
      showToast('No transactions to analyze. Please import some transactions first.', 'info');
      return;
    }
    setIsAiAnalysisModalOpen(true);
  };

  const handleStartAnalysis = async (analysisConfig) => {
    // Stream the analysis so it renders live; fall back to the job/poll path
    // on any streaming error.
    setAnalysisResults({ ai_analysis: '' });
    setAnalysisLoading(true);
    let acc = '';
    try {
      await apiService.streamTransactionAnalysis({
        accountId,
        config: analysisConfig,
        onDelta: (text) => {
          acc += text;
          setAnalysisResults({ ai_analysis: acc });
        },
        onSummary: (s) => {
          setAnalysisResults((prev) => ({
            ...(prev || {}),
            ai_analysis: acc,
            transaction_count: s.transaction_count,
            total_amount: s.total_amount,
          }));
        },
      });
      setAnalysisLoading(false);
    } catch (err) {
      console.error('Streaming AI analysis failed; falling back to job path:', err);
      try {
        setAnalysisResults(null);
        const response = await apiService.analyzeAccountTransactionsWithConfig(accountId, analysisConfig);
        setAnalysisJobId(response.job_id);
        pollAnalysisProgress(response.job_id);
      } catch (e2) {
        console.error('Fallback AI analysis failed:', e2);
        setAnalysisLoading(false);
        showToast('Failed to run AI analysis. Please try again.', 'error');
      }
    }
  };

  const pollAnalysisProgress = async (jobId) => {
    const pollInterval = setInterval(async () => {
      try {
        const status = await apiService.getTaskStatus(jobId);

        if (status.status === 'completed') {
          clearInterval(pollInterval);
          setAnalysisResults(status.result);
          setAnalysisLoading(false);
        } else if (status.status === 'failed') {
          clearInterval(pollInterval);
          setAnalysisLoading(false);
          showToast('AI analysis failed. Please try again.', 'error');
          setIsAiAnalysisModalOpen(false);
        }
      } catch (err) {
        console.error('Error polling analysis status:', err);
        clearInterval(pollInterval);
        setAnalysisLoading(false);
      }
    }, 2000); // Poll every 2 seconds

    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
    }, 300000);
  };

  const getAccountIcon = (accountType) => {
    switch (accountType) {
      case 'brokerage':
      case 'investment':
        return Building;
      case 'bank':
      case 'cash':
      case 'checking':
      case 'savings':
        return Wallet;
      case 'retirement':
      case 'credit_card':
      case 'loan':
        return CreditCard;
      default:
        return Wallet;
    }
  };

  const getAccountTypeLabel = (type) => {
    const labels = {
      'brokerage': 'Brokerage Account',
      'bank': 'Bank Account',
      'cash': 'Cash',
      'checking': 'Checking Account',
      'savings': 'Savings Account',
      'retirement': 'Retirement Account',
      'investment': 'Investment Account',
      'credit_card': 'Credit Card',
      'loan': 'Loan Account'
    };
    return labels[type] || 'Account';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: account?.currency || 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    // Parse date components directly to avoid timezone issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper to get statement info for a transaction
  const getStatementInfo = (statementId) => {
    if (!statementId) return null;
    return statements.find(s => s.id === statementId);
  };

  // Group transactions by statement for visual separators
  const getTransactionsWithSeparators = () => {
    if (statementFilter !== 'all' || transactions.length === 0) {
      return transactions.map(tx => ({ type: 'transaction', data: tx }));
    }

    const result = [];
    let currentStatementId = undefined; // Use undefined to track first iteration

    transactions.forEach((tx) => {
      // Check if we need to insert a statement header
      if (tx.statement_id !== currentStatementId) {
        currentStatementId = tx.statement_id;
        const statementInfo = getStatementInfo(tx.statement_id);

        // Add statement header
        result.push({
          type: 'statement-header',
          statementId: tx.statement_id,
          statementInfo: statementInfo
        });
      }
      result.push({ type: 'transaction', data: tx });
    });

    return result;
  };

  // Backend now provides running_balance for each transaction
  // Just reverse to show most recent first (backend sends desc order)
  const filteredTransactions = [...transactions];
  const transactionsWithSeparators = getTransactionsWithSeparators();

  if (error && !initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <p className="text-red-700 dark:text-red-400">{error || 'Account not found'}</p>
            <button
              onClick={handleBackToAccounts}
              className="mt-4 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 underline"
            >
              Back to Accounts
            </button>
          </div>
        </div>
      </div>
    );
  }

  const Icon = account ? getAccountIcon(account.account_type) : Wallet;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Back Button */}
      <div className="bg-white dark:bg-gray-800 shadow px-3 sm:px-4 lg:px-8 py-2">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={handleBackToAccounts}
            className="inline-flex items-center p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            <span className="text-sm">Back to Accounts</span>
          </button>
        </div>
      </div>

      {/* Header */}
      <PageHeader
        icon={Icon}
        title={account?.name || (initialLoading ? 'Loading...' : 'Account')}
        subtitle={account ? `${getAccountTypeLabel(account.account_type)} • ${account.institution}` : (initialLoading ? 'Please wait...' : '')}
        stats={
          <>
            {/* Account Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
              {/* Account Status */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 shadow">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Account Status</p>
                    {initialLoading ? (
                      <RefreshCw className="h-5 w-5 animate-spin text-gray-400 dark:text-gray-500 mt-1" />
                    ) : account ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        account.is_active
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                      }`}>
                        {account.is_active ? 'Active' : 'Inactive'}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Total Transactions */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 shadow">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Total Transactions</p>
                    {initialLoading ? (
                      <RefreshCw className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-gray-400 dark:text-gray-500 mt-1" />
                    ) : (
                      <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">{totalCount}</p>
                    )}
                  </div>
                  <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                </div>
              </div>

              {/* Balance Card */}
              <div>
                {initialLoading ? (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow flex items-center justify-center">
                    <RefreshCw className="h-12 w-12 animate-spin text-gray-400 dark:text-gray-500" />
                  </div>
                ) : account && (() => {
                  // Determine which balance to show
                  const selectedStatement = statementFilter !== 'all' && statementFilter !== 'none'
                    ? statements.find(s => s.id === parseInt(statementFilter))
                    : null;
                  const isCreditCard = account.account_type === 'credit_card';

                  let displayBalance;
                  let balanceLabel;

                  if (totalMarketValue > 0) {
                    displayBalance = totalMarketValue;
                    balanceLabel = 'Market Value';
                  } else if (selectedStatement) {
                    // For credit cards, statement balance represents debt (show as negative)
                    displayBalance = isCreditCard
                      ? -Math.abs(selectedStatement.statement_balance)
                      : selectedStatement.statement_balance;
                    balanceLabel = 'Statement Balance';
                  } else if (statementFilter === 'none') {
                    // "Current Statement" filter
                    if (isCreditCard) {
                      // For credit cards, show sum of unassigned transactions as debt
                      const unassignedSum = transactions.reduce((sum, tx) => sum + (tx.total_amount || 0), 0);
                      displayBalance = -Math.abs(unassignedSum);
                      balanceLabel = 'Current Balance';
                    } else {
                      // For chequing/savings, show the account balance
                      displayBalance = account.balance;
                      balanceLabel = 'Balance';
                    }
                  } else {
                    displayBalance = account.balance;
                    balanceLabel = 'Balance';
                  }

                  // Simple: negative balance = red (debt/overdraft), positive = green (good)
                  const isNegative = displayBalance < 0;

                  return (
                    <div className={`bg-gradient-to-r ${
                      isNegative
                        ? 'from-red-500 to-red-600'
                        : 'from-green-500 to-green-600'
                    } rounded-lg p-3 sm:p-4 text-white shadow`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className={`${
                            isNegative ? 'text-red-100' : 'text-green-100'
                          } text-xs sm:text-sm`}>
                            {balanceLabel}
                          </p>
                          <p className="text-xl sm:text-2xl font-semibold truncate">
                            {formatCurrency(displayBalance)}
                          </p>
                        </div>
                        <DollarSign className={`h-6 w-6 sm:h-8 sm:w-8 ${
                          isNegative ? 'text-red-200' : 'text-green-200'
                        } flex-shrink-0`} />
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </>
        }
      >
        <button
          onClick={loadAccountData}
          disabled={loading}
          className="inline-flex items-center p-2 sm:px-4 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''} sm:mr-2`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </PageHeader>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Duplicate Detection Results Card */}
      {duplicateResults && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-6 border-l-4 border-yellow-400 dark:border-yellow-500">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {duplicateResults.error ? (
                  <AlertTriangle className="h-6 w-6 text-red-500 dark:text-red-400 mr-3" />
                ) : (
                  <CheckCircle className="h-6 w-6 text-yellow-500 dark:text-yellow-400 mr-3" />
                )}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Duplicate Detection Results
                  </h3>
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {duplicateResults.error ? (
                      <p className="text-red-600">{duplicateResults.error}</p>
                    ) : duplicateResults.duplicate_count > 0 ? (
                      <div>
                        <p>Found <span className="font-semibold text-yellow-600">{duplicateResults.duplicate_count}</span> duplicate transaction pairs out of {duplicateResults.total_transactions} total transactions.</p>
                        <p className="mt-1">Duplicate transactions are highlighted in yellow and auto-selected for deletion. Click "⚠️ Potential duplicate" to see the original transaction.</p>
                      </div>
                    ) : (
                      <p className="text-green-600">✅ No duplicate transactions found! Your account looks clean.</p>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={handleDismissResults}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
                title="Dismiss"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {duplicateResults.duplicate_count > 0 && (
              <div className="mt-4 flex items-center space-x-3">
                <button
                  onClick={() => setIsDuplicateDetailsModalOpen(true)}
                  className="inline-flex items-center px-3 py-2 border border-blue-300 dark:border-blue-700 text-sm font-medium rounded-md shadow-sm text-blue-700 dark:text-blue-400 bg-white dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                >
                  View Details
                </button>
                <button
                  onClick={() => {
                    const duplicateIds = duplicates.map(d => d.duplicate_id);
                    setSelectedTransactions(new Set(duplicateIds));
                  }}
                  className="inline-flex items-center px-3 py-2 border border-yellow-300 dark:border-yellow-700 text-sm font-medium rounded-md shadow-sm text-yellow-700 dark:text-yellow-400 bg-white dark:bg-gray-700 hover:bg-yellow-50 dark:hover:bg-yellow-900/30"
                >
                  Select All Duplicates
                </button>
                {selectedTransactions.size > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    className="inline-flex items-center px-3 py-2 border border-red-300 dark:border-red-700 text-sm font-medium rounded-md shadow-sm text-red-700 dark:text-red-400 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete {selectedTransactions.size} Selected
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Holdings Section - Only show if holdings exist */}
      {holdings.length > 0 && (
        <div className="mb-6">
          <Holdings
            holdings={holdings}
            totalMarketValue={totalMarketValue}
            loading={holdingsLoading}
          />
        </div>
      )}

      {/* Transactions Section */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col gap-3">
            {/* Title and Filter Row */}
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">Transactions</h2>

              <div className="flex items-center gap-2">
                {/* Statement Filter Dropdown */}
                {statements.length > 0 && (
                  <select
                    value={statementFilter}
                    onChange={(e) => {
                      setStatementFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="block w-auto max-w-[220px] pl-3 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
                  >
                    <option value="none">Current Statement</option>
                    <option value="all">All Statements</option>
                    {statements.map((stmt) => {
                      // For credit cards, show statement balance as negative (debt)
                      const isCreditCard = account?.account_type === 'credit_card';
                      const displayBal = isCreditCard ? -Math.abs(stmt.statement_balance) : stmt.statement_balance;
                      return (
                        <option key={stmt.id} value={stmt.id}>
                          {formatDateShort(stmt.closing_date)} - ${displayBal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </option>
                      );
                    })}
                  </select>
                )}

                {/* Type Filter Dropdown */}
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="block w-auto max-w-[160px] pl-3 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-green-500 focus:border-green-500 rounded-md"
                >
                  <option value="all">All Types</option>
                  <option value="income">Income</option>
                  <option value="expense">Expenses</option>
                  <option value="deposit">Deposits</option>
                  <option value="withdrawal">Withdrawals</option>
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              {selectedTransactions.size > 0 && (
                <>
                  <span className="inline-flex items-center px-2 py-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded">
                    {selectedTransactions.size} selected
                  </span>
                  <button
                    onClick={handleBulkDelete}
                    className="inline-flex items-center px-2 sm:px-3 py-1.5 border border-red-300 dark:border-red-700 text-xs sm:text-sm font-medium rounded-md text-red-700 dark:text-red-400 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                  >
                    <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Delete</span>
                  </button>
                </>
              )}
              <button
                onClick={handleDetectDuplicates}
                disabled={detectingDuplicates || transactions.length === 0}
                className="inline-flex items-center px-2 sm:px-3 py-1.5 border border-yellow-300 dark:border-yellow-700 text-xs sm:text-sm font-medium rounded-md text-yellow-700 dark:text-yellow-400 bg-white dark:bg-gray-700 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                <span>{detectingDuplicates ? 'Detecting' : 'Duplicates'}</span>
              </button>
              <button
                onClick={() => setIsAddTransactionModalOpen(true)}
                className="inline-flex items-center px-2 sm:px-3 py-1.5 border border-green-300 dark:border-green-700 text-xs sm:text-sm font-medium rounded-md text-green-700 dark:text-green-400 bg-white dark:bg-gray-700 hover:bg-green-50 dark:hover:bg-green-900/30"
              >
                <Plus className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                <span className="hidden sm:inline">Add Transaction</span>
                <span className="sm:hidden">Add</span>
              </button>
              <button
                onClick={() => setIsAddStatementModalOpen(true)}
                className="inline-flex items-center px-2 sm:px-3 py-1.5 border border-blue-300 dark:border-blue-700 text-xs sm:text-sm font-medium rounded-md text-blue-700 dark:text-blue-400 bg-white dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/30"
              >
                <FileText className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                <span className="hidden sm:inline">Add Statement</span>
                <span className="sm:hidden">Statement</span>
              </button>
              <button
                onClick={() => setIsViewStatementsModalOpen(true)}
                className="inline-flex items-center px-2 sm:px-3 py-1.5 border border-purple-300 dark:border-purple-700 text-xs sm:text-sm font-medium rounded-md text-purple-700 dark:text-purple-400 bg-white dark:bg-gray-700 hover:bg-purple-50 dark:hover:bg-purple-900/30"
              >
                <Eye className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                <span className="hidden sm:inline">View Statements</span>
                <span className="sm:hidden">View</span>
              </button>
              <button
                onClick={() => setIsImportModalOpen(true)}
                className="inline-flex items-center px-2 sm:px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs sm:text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <Upload className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                <span>Import</span>
              </button>
              <button
                onClick={handleAiAnalysis}
                disabled={transactions.length === 0}
                className="inline-flex items-center px-2 sm:px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs sm:text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Brain className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                <span className="hidden sm:inline">AI Analysis</span>
                <span className="sm:hidden">AI</span>
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-hidden">
          {initialLoading ? (
            /* Skeleton loader for initial load */
            <div className="p-3 space-y-2 md:p-0">
              {/* Mobile skeleton */}
              <div className="md:hidden space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-white dark:bg-gray-800 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 animate-pulse">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24"></div>
                      <div className="h-5 bg-gray-300 dark:bg-gray-600 rounded w-20"></div>
                    </div>
                    <div className="h-5 bg-gray-300 dark:bg-gray-600 rounded w-full mb-1.5"></div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-16"></div>
                      <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24"></div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop skeleton */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 py-3 text-left"><div className="h-4 w-4 bg-gray-300 dark:bg-gray-600 rounded"></div></th>
                      <th className="px-3 py-3 text-left"><div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-16"></div></th>
                      <th className="px-6 py-3 text-left"><div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24"></div></th>
                      <th className="px-6 py-3 text-left"><div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-16"></div></th>
                      <th className="px-4 py-3 text-right"><div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-16 ml-auto"></div></th>
                      <th className="px-6 py-3 text-right"><div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-16 ml-auto"></div></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {[...Array(8)].map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        <td className="px-3 py-4"><div className="h-4 w-4 bg-gray-300 dark:bg-gray-600 rounded"></div></td>
                        <td className="px-3 py-4"><div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-20"></div></td>
                        <td className="px-6 py-4"><div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-full max-w-xs"></div></td>
                        <td className="px-6 py-4"><div className="h-6 bg-gray-300 dark:bg-gray-600 rounded-full w-20"></div></td>
                        <td className="px-4 py-4 text-right"><div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24 ml-auto"></div></td>
                        <td className="px-6 py-4 text-right"><div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24 ml-auto"></div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : loading && !initialLoading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-12 w-12 animate-spin mx-auto text-gray-400 dark:text-gray-500" />
              <p className="mt-4 text-gray-600 dark:text-gray-400">Refreshing account data...</p>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No transactions found</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {filterType === 'all'
                  ? 'This account has no transaction history yet.'
                  : `No ${filterType} transactions found.`
                }
              </p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden p-3 space-y-2">
                {transactionsWithSeparators.map((item, index) => {
                  if (item.type === 'statement-header') {
                    return (
                      <div
                        key={`stmt-header-${item.statementId || 'none'}-${index}`}
                        className={`flex items-center gap-2 py-2 px-3 rounded-lg ${
                          item.statementInfo
                            ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                            : 'bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600'
                        }`}
                      >
                        <FileText className={`h-4 w-4 flex-shrink-0 ${
                          item.statementInfo ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          {item.statementInfo ? (
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                Statement: {formatDate(item.statementInfo.closing_date)}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                                  {formatCurrency(item.statementInfo.statement_balance)}
                                </span>
                                {item.statementInfo.is_paid ? (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
                                    Paid
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300">
                                    Unpaid
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              No Statement Assigned
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  }

                  const transaction = item.data;
                  const isDuplicate = duplicates.some(d => d.duplicate_id === transaction.id);
                  const isHighlighted = highlightedTransaction === transaction.id;
                  const isSelected = selectedTransactions.has(transaction.id);

                  return (
                    <div
                      key={transaction.id}
                      id={`transaction-${transaction.id}`}
                      onClick={(e) => handleTransactionClick(transaction, e)}
                      className={`bg-white dark:bg-gray-800 p-2.5 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${
                        isHighlighted ? 'bg-blue-100 dark:bg-blue-900/30 ring-2 ring-blue-500 dark:ring-blue-400 border-blue-400 dark:border-blue-400' :
                        isDuplicate ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700' :
                        'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      {/* Header Row: Checkbox, Date and Amount */}
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectTransaction(transaction.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 flex-shrink-0"
                          />
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(transaction.transaction_date)}
                          </div>
                        </div>
                        <div className={`text-sm font-bold whitespace-nowrap ${
                          transaction.total_amount < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                        }`}>
                          {transaction.total_amount > 0 ? '+' : ''}{formatCurrency(transaction.total_amount)}
                        </div>
                      </div>

                      {/* Description with icon */}
                      <div className="flex items-start gap-1.5 mb-1.5">
                        {transaction.category_icon && (
                          <span className="text-base flex-shrink-0">{transaction.category_icon}</span>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
                            {transaction.description || 'Transaction'}
                          </div>
                          {isDuplicate && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowOriginal(transaction.id);
                              }}
                              className="text-xs text-yellow-600 dark:text-yellow-500 hover:text-yellow-800 dark:hover:text-yellow-400 underline"
                            >
                              ⚠️ Potential duplicate
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Category and Balance Row */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1 flex-wrap">
                          {transaction.ai_category && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 text-xs">
                              {transaction.ai_category}
                            </span>
                          )}
                          {transaction.is_statement_payment && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-xs font-medium">
                              <CreditCard className="h-3 w-3 mr-0.5" />
                              Payment
                            </span>
                          )}
                          {transaction.receipt && (
                            <button
                              onClick={(e) => handleViewReceipt(e, transaction.receipt)}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800"
                              title={`View receipt from ${transaction.receipt.merchant}`}
                            >
                              <Receipt className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Balance: <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(transaction.running_balance || 0)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-3 py-3 text-left">
                      <button
                        onClick={handleSelectAll}
                        className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        title="Select all"
                      >
                        {selectedTransactions.size === filteredTransactions.length && filteredTransactions.length > 0
                          ? <CheckSquare className="h-4 w-4" />
                          : <Square className="h-4 w-4" />
                        }
                      </button>
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Balance
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {transactionsWithSeparators.map((item, index) => {
                    if (item.type === 'statement-header') {
                      return (
                        <tr
                          key={`stmt-header-${item.statementId || 'none'}-${index}`}
                          className={`${
                            item.statementInfo
                              ? 'bg-blue-50 dark:bg-blue-900/20'
                              : 'bg-gray-100 dark:bg-gray-700/50'
                          }`}
                        >
                          <td colSpan={6} className="px-6 py-2">
                            <div className="flex items-center gap-3">
                              <FileText className={`h-4 w-4 flex-shrink-0 ${
                                item.statementInfo ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'
                              }`} />
                              {item.statementInfo ? (
                                <div className="flex items-center justify-between flex-1">
                                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                    Statement Period: {formatDate(item.statementInfo.closing_date)}
                                    {item.statementInfo.due_date && (
                                      <span className="ml-2 text-blue-600 dark:text-blue-400">
                                        (Due: {formatDate(item.statementInfo.due_date)})
                                      </span>
                                    )}
                                  </span>
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                                      Balance: {formatCurrency(item.statementInfo.statement_balance)}
                                    </span>
                                    {item.statementInfo.is_paid ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
                                        <CheckCircle className="h-3 w-3 mr-1" />
                                        Paid
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300">
                                        Unpaid
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  Transactions Not Assigned to a Statement
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    const transaction = item.data;
                    return (
                      <tr
                        key={transaction.id}
                        id={`transaction-${transaction.id}`}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${
                          highlightedTransaction === transaction.id ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-400 dark:border-blue-400' :
                          duplicates.some(d => d.duplicate_id === transaction.id) ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''
                        }`}
                        onClick={(e) => handleTransactionClick(transaction, e)}
                      >
                        <td className="px-3 py-4">
                          <input
                            type="checkbox"
                            checked={selectedTransactions.has(transaction.id)}
                            onChange={() => handleSelectTransaction(transaction.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatDate(transaction.transaction_date)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">
                                {transaction.description || 'Transaction'}
                              </span>
                              {transaction.receipt && (
                                <button
                                  onClick={(e) => handleViewReceipt(e, transaction.receipt)}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800"
                                  title={`View receipt from ${transaction.receipt.merchant}`}
                                >
                                  <Receipt className="h-3 w-3 mr-1" />
                                  Receipt
                                </button>
                              )}
                            </div>
                            {duplicates.some(d => d.duplicate_id === transaction.id) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShowOriginal(transaction.id);
                                }}
                                className="text-xs text-yellow-600 dark:text-yellow-500 hover:text-yellow-800 dark:hover:text-yellow-400 underline cursor-pointer"
                                title="Click to see original transaction"
                              >
                                ⚠️ Potential duplicate
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                              <span className="mr-1">{transaction.category_icon || '💰'}</span>
                              {transaction.ai_category || transaction.transaction_type || 'other'}
                            </span>
                            {transaction.is_statement_payment && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300">
                                <CreditCard className="h-3 w-3 mr-1" />
                                Payment
                              </span>
                            )}
                          </div>
                        </td>
                        {/* Amount Column */}
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-right">
                          {transaction.total_amount < 0 ? (
                            <span className="text-red-600 dark:text-red-400">
                              {formatCurrency(transaction.total_amount)}
                            </span>
                          ) : (
                            <span className="text-green-600 dark:text-green-400">
                              +{formatCurrency(transaction.total_amount)}
                            </span>
                          )}
                        </td>
                        {/* Balance Column */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                          <span className="text-gray-900 dark:text-white">
                            {formatCurrency(transaction.running_balance || 0)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>

              {/* Pagination Controls */}
              <Pagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={setCurrentPage}
                onPageSizeChange={(newSize) => {
                  setPageSize(newSize);
                  setCurrentPage(1);
                }}
              />
            </>
          )}
        </div>
      </div>
      </div>

      <ImportTransactionsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        account={account}
        onTransactionsImported={handleTransactionsImported}
      />

      <AddTransactionModal
        isOpen={isAddTransactionModalOpen}
        onClose={() => setIsAddTransactionModalOpen(false)}
        account={account}
        categoryOptions={categoryOptions}
        onTransactionAdded={handleTransactionAdded}
      />

      <AddStatementModal
        isOpen={isAddStatementModalOpen}
        onClose={() => setIsAddStatementModalOpen(false)}
        accountId={accountId}
        onStatementAdded={loadStatements}
      />

      <ViewStatementsModal
        isOpen={isViewStatementsModalOpen}
        onClose={() => setIsViewStatementsModalOpen(false)}
        statements={statements}
        loading={statementsLoading}
        onMarkPaid={handleMarkStatementPaid}
        onMarkUnpaid={handleMarkStatementUnpaid}
        onUpdate={handleUpdateStatement}
        onDelete={handleDeleteStatement}
        onRecalculateDueDates={handleRecalculateDueDates}
        hasPaymentDueDay={!!account?.payment_due_day}
      />

      <AiAnalysisModal
        isOpen={isAiAnalysisModalOpen}
        onClose={() => {
          setIsAiAnalysisModalOpen(false);
          setAnalysisResults(null);
          setAnalysisJobId(null);
          setAnalysisLoading(false);
        }}
        analysisResults={analysisResults}
        isLoading={analysisLoading}
        onStartAnalysis={handleStartAnalysis}
        categoryOptions={categoryOptions}
      />

      <BulkCategoryUpdateModal
        isOpen={bulkUpdateModal.isOpen}
        onClose={handleBulkUpdateCancel}
        onConfirm={handleBulkUpdateConfirm}
        accountId={accountId}
        description={bulkUpdateModal.description}
        newCategory={bulkUpdateModal.newCategory}
        newCategoryIcon={bulkUpdateModal.newCategoryIcon}
        categoryLabel={bulkUpdateModal.categoryLabel}
      />

      <TransactionDetailsModal
        isOpen={isTransactionModalOpen}
        onClose={() => {
          setIsTransactionModalOpen(false);
          setSelectedTransaction(null);
        }}
        transaction={selectedTransaction}
        categoryOptions={categoryOptions}
        onSave={handleTransactionSave}
        onDelete={handleTransactionDelete}
        onCategoryCreated={loadCategories}
        onTransactionUpdated={loadTransactions}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
        onMarkStatementEnd={handleMarkStatementEnd}
      />

      <DuplicateDetailsModal
        isOpen={isDuplicateDetailsModalOpen}
        onClose={() => setIsDuplicateDetailsModalOpen(false)}
        duplicates={duplicates}
        transactions={transactions}
        onBulkDelete={async (transactionIds) => {
          try {
            await apiService.bulkDeleteTransactions(transactionIds);

            // Refresh the transaction list
            await loadTransactions();

            // Clear all state
            setSelectedTransactions(new Set());
            setDuplicates([]);
            setHighlightedTransaction(null);
            setDuplicateResults(null);
          } catch (error) {
            console.error('Error in onBulkDelete:', error);
            throw error;
          }
        }}
      />

      <ReceiptModal
        isOpen={viewReceiptModal.isOpen}
        onClose={handleCloseReceiptModal}
        receipt={viewReceiptModal.receipt}
        accounts={accounts}
        currentAccount={account}
        onReceiptUpdated={loadTransactions}
      />

      <ConfirmationModal />
      <ToastContainer />
    </div>
  );
};

export default AccountTab;